import {css, Tab, Tabs, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import {getIconComponentByName, GetStylesFunction} from '@wallaby-ui/core';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

export type LightFilterTabDef = {
  /** Stable id stored in the page state (URL / localStorage) as the active tab. */
  id: string;
  /** Translated label. */
  label: string;
  /** Icon from the core icon set (src/assets/icons), rendered before the label. */
  iconName: string;
  /** Active filters inside this tab; the badge is hidden at 0. */
  count?: number;
  /** Kept in the strip definition but not rendered (display: none), e.g. a tab whose filters are not implemented yet. */
  hidden?: boolean;
};

type Props = {
  tabs: LightFilterTabDef[];
  activeTab: string;
  onChange: (tabId: string) => void;
  dataName?: string;
};

/**
 * Tab strip of a tabbed filter panel (PDM-2750): one pill per filter group - icon, label and a badge with the
 * number of active filters in the group. Built on MUI Tabs, so it keeps the app's tab colors and keyboard
 * handling; the size is the compact one of the "Filters" header row, not the 48px page tabs.
 *
 * Compact mode is automatic: when the strip with every label would not fit the width it gets, the INACTIVE tabs
 * drop their label (icon + badge only) while the active tab keeps it. The full-label width is measured on a
 * hidden copy of the strip, so the visible strip never flickers between the two states. If even the compact
 * strip is too wide (a very narrow pane), the pills wrap onto a second line rather than being clipped.
 */
export const LightFilterTabs: React.FC<Props> = (props) => {
  const {tabs, activeTab, onChange, dataName = 'light-filter-tabs'} = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const [compact, setCompact] = useState(false);
  const measure = useCallback(() => {
    const container = containerRef.current;
    const ghost = ghostRef.current;
    if (!container || !ghost) return;
    // The ghost is the strip with all labels; +1 absorbs sub-pixel rounding of the flex layout.
    setCompact(ghost.scrollWidth > container.clientWidth + 1);
  }, []);
  const labelsKey = tabs.map((tab) => `${tab.id}:${tab.label}:${tab.count ?? 0}`).join('|');
  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // Re-measure when a label or a count changes width, not only on resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, labelsKey]);
  const renderTab = (tab: LightFilterTabDef, showLabel: boolean, ghost: boolean) => {
    const count = tab.count ?? 0;
    const IconComponent = getIconComponentByName(tab.iconName);
    return (
      <Tab
        key={tab.id}
        value={tab.id}
        css={muiStyles.tab}
        icon={IconComponent ? <SvgIcon component={IconComponent} inheritViewBox css={muiStyles.tabIcon} /> : undefined}
        iconPosition='start'
        label={
          <span css={muiStyles.tabLabel}>
            {showLabel && <span>{tab.label}</span>}
            {count > 0 && (
              <span css={muiStyles.tabBadge} data-name='light-filter-tabs--badge'>
                {count}
              </span>
            )}
          </span>
        }
        title={showLabel ? undefined : tab.label}
        onClick={ghost ? undefined : () => onChange(tab.id)}
        // The measuring copy must never take focus or clicks.
        disabled={ghost}
        tabIndex={ghost ? -1 : undefined}
        data-name={`${dataName}--tab`}
        data-tab-id={tab.id}
        style={tab.hidden ? {display: 'none'} : undefined}
      />
    );
  };
  return (
    <div ref={containerRef} css={muiStyles.container} data-name={dataName} data-compact={compact}>
      <Tabs value={activeTab} css={muiStyles.tabs}>
        {tabs.map((tab) => renderTab(tab, !compact || tab.id === activeTab, false))}
      </Tabs>
      {/* Hidden full-label copy used only for measuring; not visible, not focusable, not announced. */}
      <div ref={ghostRef} css={muiStyles.ghost} aria-hidden>
        <Tabs value={activeTab} css={muiStyles.tabs}>
          {tabs.map((tab) => renderTab(tab, true, true))}
        </Tabs>
      </div>
    </div>
  );
};
LightFilterTabs.displayName = 'LightFilterTabs';

const getStyles: GetStylesFunction = (_options) => ({
  container: css`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    /* Last resort on a very narrow pane: wrap the compact pills instead of clipping them. */
    &[data-compact='true'] .MuiTabs-flexContainer {
      flex-wrap: wrap;
    }
  `,
  ghost: css`
    position: absolute;
    top: 0;
    left: 0;
    visibility: hidden;
    pointer-events: none;
    white-space: nowrap;
    /* Natural width of the strip, unconstrained by the container. */
    width: max-content;
  `,
  // MUI Tabs default to 48px; this strip lives in the 32px "Filters" header row, so the pills define the height.
  tabs: css`
    min-height: 0;
    & .MuiTabs-scroller {
      overflow: visible !important;
    }
    & .MuiTabs-flexContainer {
      gap: 8px;
      row-gap: 8px;
    }
  `,
  // Pill: the app's tab shape (radius 8, teal when selected - from the theme) at the filter-header size. The
  // inactive fill is gray-50 like the page tabs (.dl-tab-row) and the icon buttons around it.
  tab: css`
    font-family: SourceSansPro-Semibold;
    font-size: 14px;
    line-height: 22px;
    padding: 8px 12px;
    min-height: 0;
    min-width: 0;
    gap: 8px;
    background-color: var(--dlThemeGray-50);
    white-space: nowrap;
    &.Mui-disabled {
      opacity: 1;
    }
    & .MuiTab-iconWrapper {
      margin: 0;
    }
  `,
  tabIcon: css`
    width: 14px;
    height: 14px;
    font-size: 14px;
  `,
  tabLabel: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
  `,
  // Count badge: white on an inactive pill, translucent white on the selected (teal) one.
  tabBadge: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    box-sizing: border-box;
    border-radius: 9px;
    font-family: SourceSansPro-Semibold;
    font-size: 11px;
    line-height: 18px;
    background: #ffffff;
    color: var(--dlTheme-600);
    .Mui-selected & {
      background: rgba(255, 255, 255, 0.22);
      color: #ffffff;
    }
    button:hover:not(.Mui-selected) & {
      background: rgba(255, 255, 255, 0.22);
      color: #ffffff;
    }
  `,
});
